import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  type Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { Checkbox2Component } from '@frontendlib/components/controls/checkbox/checkbox2.component';
import { DropdownInputComponent } from '@frontendlib/components/controls/dropdown-input/dropdown-input.component';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import type { CalendarSummaryDto } from '@sharedlib/dtos/dto/calendar-app/calendar.dto';
import { Functions } from '@sharedlib/common/lang/Functions';
import { Implements } from '@sharedlib/common/lang/Implements';

export type CalendarsPickerValue = string | string[] | null;

@Component({
  selector: 'app-calendars-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendars-picker.component.html',
  imports: [DropdownInputComponent, Checkbox2Component],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarsPickerComponent),
      multi: true,
    },
  ],
})
export class CalendarsPickerComponent implements ControlValueAccessor {
  public readonly multiple = input<boolean>(false);
  public readonly placeholder = input<string>('Select calendar');
  public readonly clear = input<boolean>(false);
  public readonly render = input<'icon' | 'text' | 'icon-text'>('text');
  public readonly appearance = input<
    'default' | 'amethyst' | 'amethyst-simple' | 'sapphire' | 'sapphire-outline' | 'sapphire-inline' | 'sapphire-dark'
  >('sapphire-inline');
  public readonly size = input<'s' | 'm' | 'l'>('m');

  public readonly value = model<CalendarsPickerValue>(null);

  private readonly isDisabled = signal<boolean>(false);

  private onChange: Functions.Consumer<CalendarsPickerValue> = Functions.noop;
  private onTouched: Functions.Callback = Functions.noop;

  private readonly calendarsService = inject(CalendarsService);

  protected readonly calendars = toSignal(this.calendarsService.findAll({ short: true }));

  protected readonly calendarsById: Signal<Map<string, CalendarSummaryDto>> = computed(() => {
    const map = new Map<string, CalendarSummaryDto>();
    for (const cal of this.calendars() ?? []) {
      map.set(cal.id, cal);
    }
    return map;
  });

  protected readonly selectedItems: Signal<CalendarSummaryDto[]> = computed(() => {
    const val = this.value();
    const lookup = this.calendarsById();
    if (Array.isArray(val)) {
      return val.map((id) => lookup.get(id)).filter(Boolean) as CalendarSummaryDto[];
    }
    if (val) {
      const cal = lookup.get(val);
      return cal ? [cal] : [];
    }
    return [];
  });

  protected readonly options: Signal<DropdownSelectItem<CalendarSummaryDto>[]> = computed(
    () =>
      this.calendars()?.map((calendar) => ({
        title: calendar.title,
        value: calendar,
        source: calendar,
      })) ?? [],
  );

  protected readonly selectedIds: Signal<Set<string>> = computed(() => {
    const val = this.value();
    if (Array.isArray(val)) {
      return new Set(val);
    }
    return val ? new Set([val]) : new Set<string>();
  });

  protected readonly dropdownValue: Signal<CalendarSummaryDto | null> = computed(() => {
    if (this.multiple()) {
      return null;
    }
    return this.selectedItems()[0] ?? null;
  });

  protected readonly multipleDisplayText: Signal<string> = computed(() => {
    const items = this.selectedItems();
    if (items.length === 0) {
      return '';
    }
    if (items.length === 1) {
      return items[0].title;
    }
    if (items.length === this.calendars()?.length) {
      return 'All calendars';
    } else {
      return `${items.length} calendars`;
    }
  });

  protected readonly disabled: Signal<boolean> = computed(() => this.isDisabled());

  constructor() {
    effect(() => {
      const value = this.value();
      const calendars = this.calendars();
      if (!this.multiple() || !Array.isArray(value) || !calendars) return;
      const selected = new Set(value);
      const ordered = calendars.map(({ id }) => id).filter((id) => selected.has(id));
      // Preserve unresolved IDs until the list has loaded them; only normalize complete selections.
      if (ordered.length !== value.length || ordered.every((id, index) => id === value[index])) return;
      this.value.set(ordered);
      this.onChange(ordered);
    });
  }

  @(Implements<ControlValueAccessor>)
  public writeValue(val: CalendarsPickerValue): void {
    if (this.multiple()) {
      this.value.set(Array.isArray(val) ? val : []);
    } else {
      this.value.set(val && !Array.isArray(val) ? val : null);
    }
  }

  @(Implements<ControlValueAccessor>)
  public registerOnChange(fn: Functions.Consumer<CalendarsPickerValue>): void {
    this.onChange = fn;
  }

  @(Implements<ControlValueAccessor>)
  public registerOnTouched(fn: Functions.Callback): void {
    this.onTouched = fn;
  }

  @(Implements<ControlValueAccessor>)
  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected handleSingleSelect(selectedValue: CalendarSummaryDto): void {
    const id = selectedValue.id;
    this.value.set(id);
    this.onChange(id);
    this.onTouched();
  }

  protected handleMultiToggle(calendar: CalendarSummaryDto, checked: boolean): void {
    const currentValue = this.value();
    const selected = new Set(Array.isArray(currentValue) ? currentValue : []);
    if (checked) selected.add(calendar.id);
    else selected.delete(calendar.id);
    // Selection order is the fallback order when the last activated calendar is unchecked.
    const updated = (this.calendars() ?? []).map(({ id }) => id).filter((id) => selected.has(id));
    this.value.set(updated);
    this.onChange(updated);
    this.onTouched();
  }

  protected isSelected(calendarId: string): boolean {
    return this.selectedIds().has(calendarId);
  }
}
